import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Radio, Eye, Copy, RefreshCw, CheckCircle, XCircle, Archive } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

const STATUS_LABELS = {
  draft: { label: 'Draft', cls: 'text-gray-400 bg-white/5' },
  scheduled: { label: 'Scheduled', cls: 'text-blue-300 bg-blue-500/10' },
  live: { label: 'LIVE', cls: 'text-red-300 bg-red-500/10 animate-pulse' },
  ended: { label: 'Ended', cls: 'text-yellow-300 bg-yellow-500/10' },
  processing: { label: 'Processing', cls: 'text-orange-300 bg-orange-500/10' },
  published: { label: 'Published', cls: 'text-green-300 bg-green-500/10' },
  cancelled: { label: 'Cancelled', cls: 'text-gray-500 bg-white/5' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || { label: status, cls: 'text-gray-400 bg-white/5' };
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-sm ${s.cls}`}>
      {s.label}
    </span>
  );
};

const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error('Copy failed'));
};

const LiveSeminarsManagement = () => {
  const { token } = useAuth();
  const [seminars, setSeminars] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatTab, setChatTab] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchSeminars = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/live-seminars`, { headers: authHeaders });
      setSeminars(res.data);
    } catch (e) {
      toast.error('Failed to load live seminars');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plans`, { headers: authHeaders });
      setPlans(res.data || []);
    } catch (_e) {}
  }, [token]);

  useEffect(() => { fetchSeminars(); fetchPlans(); }, [fetchSeminars, fetchPlans]);

  const fetchChat = async (seminarId) => {
    try {
      const res = await axios.get(`${API}/admin/live-seminars/${seminarId}/chat`, { headers: authHeaders });
      setChatMessages(res.data);
      setChatTab(seminarId);
    } catch (e) {
      toast.error('Failed to load chat');
    }
  };

  const hideMessage = async (seminarId, msgId) => {
    try {
      await axios.post(`${API}/admin/live-seminars/${seminarId}/chat/${msgId}/hide`, {}, { headers: authHeaders });
      setChatMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, status: 'hidden' } : m));
      toast.success('Message hidden');
    } catch (_e) { toast.error('Failed to hide message'); }
  };

  const markAnswered = async (seminarId, msgId) => {
    try {
      await axios.post(`${API}/admin/live-seminars/${seminarId}/chat/${msgId}/mark-answered`, {}, { headers: authHeaders });
      setChatMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, status: 'answered' } : m));
      toast.success('Marked answered');
    } catch (_e) { toast.error('Failed to mark answered'); }
  };

  const regenerateKey = async (seminarId) => {
    if (!window.confirm('Regenerate stream key? OBS must be reconfigured.')) return;
    try {
      const res = await axios.post(`${API}/admin/live-seminars/${seminarId}/regenerate-stream-key`, {}, { headers: authHeaders });
      toast.success('Stream key regenerated');
      setSeminars((prev) => prev.map((s) => s.id === seminarId ? { ...s, ...res.data } : s));
      if (selected?.id === seminarId) setSelected((p) => ({ ...p, ...res.data }));
    } catch (_e) { toast.error('Failed to regenerate key'); }
  };

  const setStatus = async (seminarId, action) => {
    const labels = { start: 'Start Live', end: 'End Live', 'publish-replay': 'Publish Replay', archive: 'Archive' };
    if (!window.confirm(`${labels[action] || action}?`)) return;
    try {
      await axios.post(`${API}/admin/live-seminars/${seminarId}/${action}`, {}, { headers: authHeaders });
      toast.success(`${labels[action] || action} successful`);
      await fetchSeminars();
      if (selected?.id === seminarId) {
        const res = await axios.get(`${API}/admin/live-seminars/${seminarId}`, { headers: authHeaders });
        setSelected(res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-roboto font-bold text-2xl text-white">Live Seminars</h1>
          <p className="text-gray-500 text-sm mt-0.5">Manage live streams, OBS credentials, and replays</p>
        </div>
        <Button onClick={() => { setSelected(null); setFormOpen(true); }} className="flex items-center gap-2">
          <Plus size={16} />
          New Live Seminar
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-misel-card animate-pulse rounded-sm" />
          ))}
        </div>
      ) : seminars.length === 0 ? (
        <div className="text-center py-16 text-gray-500">No live seminars yet. Create one to get started.</div>
      ) : (
        <div className="space-y-3">
          {seminars.map((s) => (
            <div
              key={s.id}
              className="bg-misel-card border border-white/10 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white text-sm truncate">{s.title}</span>
                  <StatusBadge status={s.status} />
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-2">
                  <span>/{s.slug}</span>
                  {s.scheduled_start_at && (
                    <span>{new Date(s.scheduled_start_at).toLocaleString()}</span>
                  )}
                  {s.access_plan_names?.length > 0 && (
                    <span className="text-gray-600">{s.access_plan_names.join(', ')}</span>
                  )}
                  {s.replay_available && <span className="text-green-400">Replay ready</span>}
                  {s.is_ppv_enabled && (
                    <span className="text-primary">
                      PPV {Number(s.ppv_price || 0).toFixed(2)} {s.ppv_currency || 'EUR'}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
                  onClick={() => { setSelected(s); setFormOpen(false); setChatTab(null); }}
                >
                  <Edit size={13} className="mr-1" />
                  Manage
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-[#111111] border border-white/15 text-white max-w-xl w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected ? 'Edit Live Seminar' : 'New Live Seminar'}</DialogTitle>
          </DialogHeader>
          <LiveSeminarForm
            initial={selected}
            plans={plans}
            authHeaders={authHeaders}
            onSaved={async () => {
              setFormOpen(false);
              await fetchSeminars();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Detail / manage panel */}
      {selected && !formOpen && (
        <LiveSeminarDetail
          seminar={selected}
          plans={plans}
          authHeaders={authHeaders}
          chatMessages={chatMessages}
          chatTab={chatTab}
          onFetchChat={fetchChat}
          onHide={hideMessage}
          onMarkAnswered={markAnswered}
          onRegenerateKey={regenerateKey}
          onSetStatus={setStatus}
          onEdit={() => setFormOpen(true)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

// Form
const LiveSeminarForm = ({ initial, plans, authHeaders, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: initial?.title || '',
    slug: initial?.slug || '',
    description: initial?.description || '',
    scheduled_start_at: initial?.scheduled_start_at || '',
    scheduled_end_at: initial?.scheduled_end_at || '',
    access_plan_ids: initial?.access_plan_ids || [],
    hls_playback_url: initial?.hls_playback_url || '',
    vod_video_id: initial?.vod_video_id || '',
    vod_hls_url: initial?.vod_hls_url || '',
    chat_enabled: initial?.chat_enabled ?? true,
    questions_enabled: initial?.questions_enabled ?? true,
    replay_available: initial?.replay_available ?? false,
    is_ppv_enabled: initial?.is_ppv_enabled ?? false,
    ppv_price: initial?.ppv_price || '',
    ppv_currency: initial?.ppv_currency || 'EUR',
    ppv_includes_replay: initial?.ppv_includes_replay ?? true,
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const togglePlan = (id) => {
    set('access_plan_ids', form.access_plan_ids.includes(id)
      ? form.access_plan_ids.filter((p) => p !== id)
      : [...form.access_plan_ids, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.hls_playback_url) delete payload.hls_playback_url;
      if (!payload.vod_video_id) delete payload.vod_video_id;
      if (!payload.vod_hls_url) delete payload.vod_hls_url;
      payload.ppv_price = payload.ppv_price === '' ? null : Number(payload.ppv_price);
      payload.ppv_currency = (payload.ppv_currency || 'EUR').toUpperCase();

      if (initial?.id) {
        await axios.put(`${API}/admin/live-seminars/${initial.id}`, payload, { headers: authHeaders });
        toast.success('Live seminar updated');
      } else {
        await axios.post(`${API}/admin/live-seminars`, payload, { headers: authHeaders });
        toast.success('Live seminar created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'block text-xs text-gray-400 mb-1';
  const inputCls = 'w-full bg-white/5 border border-white/15 text-white rounded-sm text-sm';

  return (
    <form onSubmit={submit} className="space-y-4 mt-2">
      <div>
        <label className={labelCls}>Title *</label>
        <Input value={form.title} onChange={(e) => set('title', e.target.value)} required className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Slug * (URL path)</label>
        <Input value={form.slug} onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} required className={inputCls} placeholder="my-live-seminar" />
      </div>
      <div>
        <label className={labelCls}>Description</label>
        <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} className={inputCls} rows={3} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Scheduled Start</label>
          <Input type="datetime-local" value={form.scheduled_start_at} onChange={(e) => set('scheduled_start_at', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Scheduled End</label>
          <Input type="datetime-local" value={form.scheduled_end_at} onChange={(e) => set('scheduled_end_at', e.target.value)} className={inputCls} />
        </div>
      </div>

      <div>
        <label className={labelCls}>Access Plans (which subscribers can watch)</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {plans.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => togglePlan(p.id)}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                form.access_plan_ids.includes(p.id)
                  ? 'bg-primary border-primary text-white'
                  : 'border-white/20 text-gray-400 hover:border-white/40'
              }`}
            >
              {p.name}
            </button>
          ))}
          {plans.length === 0 && <span className="text-gray-500 text-xs">No plans loaded</span>}
        </div>
        {form.access_plan_ids.length === 0 && (
          <p className="text-xs text-red-400 mt-1 font-medium">
            ⚠ No access plans selected. This live seminar is locked for all students until at least one plan is selected.
          </p>
        )}
      </div>

      <div className="border-t border-white/10 pt-4 space-y-3">
        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Manual Stream / Replay URLs (Phase 1)</p>
        <div>
          <label className={labelCls}>HLS Playback URL (for live)</label>
          <Input value={form.hls_playback_url} onChange={(e) => set('hls_playback_url', e.target.value)} className={inputCls} placeholder="https://..." />
        </div>
        <div>
          <label className={labelCls}>VOD HLS URL (for replay)</label>
          <Input value={form.vod_hls_url} onChange={(e) => set('vod_hls_url', e.target.value)} className={inputCls} placeholder="https://..." />
        </div>
        <div>
          <label className={labelCls}>VOD Video ID (existing platform video)</label>
          <Input value={form.vod_video_id} onChange={(e) => set('vod_video_id', e.target.value)} className={inputCls} placeholder="optional video id" />
        </div>
      </div>

      <div className="border-t border-white/10 pt-4 space-y-3">
        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">One-time purchase access</p>
        <p className="text-xs text-yellow-400">
          Subscribers in selected plans get access automatically. PPV is for non-subscribers.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={form.is_ppv_enabled} onChange={(e) => set('is_ppv_enabled', e.target.checked)} className="accent-primary" />
          Enable PPV
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>PPV Price</label>
            <Input type="number" min="0" step="0.01" value={form.ppv_price} onChange={(e) => set('ppv_price', e.target.value)} className={inputCls} placeholder="29.00" />
          </div>
          <div>
            <label className={labelCls}>Currency</label>
            <Input value={form.ppv_currency} onChange={(e) => set('ppv_currency', e.target.value.toUpperCase())} className={inputCls} placeholder="EUR" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={form.ppv_includes_replay} onChange={(e) => set('ppv_includes_replay', e.target.checked)} className="accent-primary" />
          Includes replay access
        </label>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={form.chat_enabled} onChange={(e) => set('chat_enabled', e.target.checked)} className="accent-primary" />
          Enable chat
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={form.questions_enabled} onChange={(e) => set('questions_enabled', e.target.checked)} className="accent-primary" />
          Enable questions
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={form.replay_available} onChange={(e) => set('replay_available', e.target.checked)} className="accent-primary" />
          Replay available
        </label>
      </div>

      <Button type="submit" disabled={saving} className="w-full">
        {saving ? 'Saving…' : initial?.id ? 'Save Changes' : 'Create Live Seminar'}
      </Button>
    </form>
  );
};

// Detail / manage panel
const LiveSeminarDetail = ({
  seminar, plans, authHeaders, chatMessages, chatTab,
  onFetchChat, onHide, onMarkAnswered, onRegenerateKey, onSetStatus, onEdit, onClose,
}) => {
  const planNames = plans.reduce((acc, p) => ({ ...acc, [p.id]: p.name }), {});
  const [streamStatus, setStreamStatus] = useState(null);
  const [checkingStream, setCheckingStream] = useState(false);
  const infraWarnings = seminar.live_infrastructure_warnings || [];
  const obsServerUrl = seminar.obs_server_url || '';
  const expectedHlsUrl = seminar.expected_hls_playback_url || seminar.hls_playback_url || '';

  const checkStreamStatus = async () => {
    setCheckingStream(true);
    try {
      const res = await axios.get(`${API}/admin/live-seminars/${seminar.id}/stream-status`, { headers: authHeaders });
      setStreamStatus(res.data);
      if (res.data.hls_reachable === true) toast.success('HLS stream is reachable');
      else if (res.data.hls_configured) toast.warning('HLS stream is not reachable yet');
      else toast.warning('HLS stream is not configured');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stream status check failed');
    } finally {
      setCheckingStream(false);
    }
  };

  return (
    <div className="bg-misel-card border border-white/10 rounded-sm p-5 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-roboto font-bold text-xl text-white">{seminar.title}</h2>
            <StatusBadge status={seminar.status} />
          </div>
          <p className="text-gray-500 text-xs mt-0.5">/{seminar.slug}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 h-8 text-xs" onClick={onEdit}>
            <Edit size={13} className="mr-1" /> Edit
          </Button>
          <Button size="sm" variant="outline" className="border-white/20 text-gray-400 hover:bg-white/10 h-8 text-xs" onClick={onClose}>
            ✕
          </Button>
        </div>
      </div>

      {seminar.description && (
        <p className="text-gray-400 text-sm">{seminar.description}</p>
      )}

      {/* Access plans */}
      {seminar.access_plan_ids?.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Access Plans</p>
          <div className="flex flex-wrap gap-2">
            {seminar.access_plan_ids.map((id) => (
              <span key={id} className="text-xs bg-primary/10 border border-primary/30 text-primary px-2 py-0.5 rounded-sm">
                {planNames[id] || id}
              </span>
            ))}
          </div>
        </div>
      )}
      {seminar.access_plan_ids?.length === 0 && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-xs p-3 rounded-sm">
          No access plans selected. This live seminar is locked for students unless PPV is enabled; admins can still preview it.
        </div>
      )}

      <div className="bg-black/20 border border-white/10 rounded-sm p-4 space-y-2">
        <p className="text-sm font-semibold text-white">One-time Purchase Access</p>
        {seminar.is_ppv_enabled ? (
          <>
            <p className="text-xs text-primary font-semibold">
              PPV enabled: {Number(seminar.ppv_price || 0).toFixed(2)} {seminar.ppv_currency || 'EUR'}
            </p>
            <p className="text-xs text-gray-500">
              {seminar.ppv_includes_replay ? 'Includes replay access when published.' : 'Live access only; replay not included.'}
            </p>
          </>
        ) : (
          <p className="text-xs text-gray-500">PPV disabled. Non-subscribers cannot buy standalone access.</p>
        )}
      </div>

      {/* OBS Credentials */}
      <div className="bg-black/20 border border-white/10 rounded-sm p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Radio size={14} className="text-red-400" />
            <p className="text-sm font-semibold text-white">OBS Setup</p>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-sm border ${
            seminar.live_infrastructure_configured
              ? 'border-green-500/30 bg-green-500/10 text-green-300'
              : 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300'
          }`}>
            {seminar.live_infrastructure_configured ? 'Infrastructure configured' : 'Infrastructure not configured'}
          </span>
        </div>
        {infraWarnings.length > 0 && (
          <div className="space-y-1">
            {infraWarnings.map((warning) => (
              <p key={warning} className="text-xs text-yellow-400 font-semibold">{warning}</p>
            ))}
          </div>
        )}
        <p className="text-xs text-yellow-400 font-semibold">
          ⚠ Do not share the stream key with students. It is for OBS configuration only.
        </p>

        <div>
          <p className="text-xs text-gray-500 mb-1">Server URL (OBS → Settings → Stream)</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/40 border border-white/10 rounded-sm px-3 py-2 text-xs text-green-300 font-mono truncate">
              {obsServerUrl || 'Configure LIVE_RTMP_BASE_URL before going live'}
            </code>
            {obsServerUrl && (
              <button
                onClick={() => copyToClipboard(obsServerUrl, 'Server URL')}
                className="p-2 hover:bg-white/10 rounded-sm transition-colors"
                title="Copy server URL"
              >
                <Copy size={14} className="text-gray-400" />
              </button>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-1">Expected HLS Playback URL</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/40 border border-white/10 rounded-sm px-3 py-2 text-xs text-blue-300 font-mono truncate">
              {expectedHlsUrl || 'Configure LIVE_HLS_BASE_URL or set a manual HLS URL'}
            </code>
            {expectedHlsUrl && (
              <button
                onClick={() => copyToClipboard(expectedHlsUrl, 'HLS playback URL')}
                className="p-2 hover:bg-white/10 rounded-sm transition-colors"
                title="Copy HLS playback URL"
              >
                <Copy size={14} className="text-gray-400" />
              </button>
            )}
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-1">Stream Key (OBS → Settings → Stream)</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/40 border border-white/10 rounded-sm px-3 py-2 text-xs text-yellow-300 font-mono truncate">
              {seminar.obs_stream_key || '—'}
            </code>
            <button
              onClick={() => copyToClipboard(seminar.obs_stream_key, 'Stream key')}
              className="p-2 hover:bg-white/10 rounded-sm transition-colors"
              title="Copy stream key"
            >
              <Copy size={14} className="text-gray-400" />
            </button>
            <button
              onClick={() => onRegenerateKey(seminar.id)}
              className="p-2 hover:bg-white/10 rounded-sm transition-colors"
              title="Regenerate stream key"
            >
              <RefreshCw size={14} className="text-gray-400" />
            </button>
          </div>
        </div>

        <div className="text-xs text-gray-500 space-y-0.5 border-t border-white/10 pt-3">
          <p className="font-semibold text-gray-400 mb-1">OBS Setup (Custom RTMP):</p>
          <p>Service: Custom</p>
          <p>Server: <span className="text-gray-300">{obsServerUrl || 'Configure LIVE_RTMP_BASE_URL'}</span></p>
          <p>Stream Key: <span className="text-yellow-300">{seminar.obs_stream_key || '-'}</span></p>
        </div>
      </div>

      {/* Stream status */}
      <div className="bg-black/20 border border-white/10 rounded-sm p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-white">Stream Status</p>
            <p className="text-xs text-gray-500">Checks the expected HLS playlist without exposing the OBS key publicly.</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={checkingStream}
            className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
            onClick={checkStreamStatus}
          >
            <RefreshCw size={13} className={`mr-1 ${checkingStream ? 'animate-spin' : ''}`} />
            {checkingStream ? 'Checking...' : 'Check Stream Status'}
          </Button>
        </div>
        {streamStatus && (
          <div className="text-xs space-y-1">
            <p className="text-gray-500">HLS URL: <span className="text-gray-300 break-all">{streamStatus.hls_url || 'Not configured'}</span></p>
            <p className={streamStatus.hls_reachable === true ? 'text-green-300' : streamStatus.hls_configured ? 'text-yellow-300' : 'text-gray-400'}>
              {streamStatus.hls_reachable === true ? 'Reachable' : streamStatus.hls_configured ? 'Not reachable yet' : 'Not configured'}
            </p>
            <p className="text-gray-500">{streamStatus.message}</p>
          </div>
        )}
      </div>

      {/* Workflow */}
      <div className="bg-black/20 border border-white/10 rounded-sm p-4">
        <p className="text-sm font-semibold text-white mb-2">Live Workflow</p>
        <ol className="list-decimal list-inside text-xs text-gray-400 space-y-1">
          <li>Create the live seminar and select access plans.</li>
          <li>Copy the OBS server URL and stream key into OBS.</li>
          <li>Start the live seminar, then check stream status.</li>
          <li>End the live seminar when finished.</li>
          <li>Attach or publish the replay when the recording is ready.</li>
        </ol>
      </div>

      {/* Replay handoff */}
      <div className="bg-black/20 border border-white/10 rounded-sm p-4 space-y-2">
        <p className="text-sm font-semibold text-white">Replay Handoff</p>
        <p className="text-xs text-gray-500">VOD HLS URL: <span className="text-gray-300 break-all">{seminar.vod_hls_url || 'Not linked'}</span></p>
        <p className="text-xs text-gray-500">Linked Video ID: <span className="text-gray-300 break-all">{seminar.vod_video_id || 'Not linked'}</span></p>
        <p className="text-xs text-gray-500">Replay available: <span className={seminar.replay_available ? 'text-green-300' : 'text-gray-400'}>{seminar.replay_available ? 'Yes' : 'No'}</span></p>
      </div>

      {/* Status Controls */}
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Status Controls</p>
        <div className="flex flex-wrap gap-2">
          {['draft', 'scheduled'].includes(seminar.status) && (
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 gap-1"
              onClick={() => onSetStatus(seminar.id, 'start')}
            >
              <Radio size={12} /> Start Live
            </Button>
          )}
          {seminar.status === 'live' && (
            <Button
              size="sm"
              className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-8 gap-1"
              onClick={() => onSetStatus(seminar.id, 'end')}
            >
              <XCircle size={12} /> End Live
            </Button>
          )}
          {['ended', 'processing'].includes(seminar.status) && (
            <Button
              size="sm"
              className="bg-green-700 hover:bg-green-800 text-white text-xs h-8 gap-1"
              onClick={() => onSetStatus(seminar.id, 'publish-replay')}
            >
              <CheckCircle size={12} /> Publish Replay
            </Button>
          )}
          {seminar.status !== 'cancelled' && (
            <Button
              size="sm"
              variant="outline"
              className="border-white/20 text-gray-400 hover:bg-white/10 text-xs h-8 gap-1"
              onClick={() => onSetStatus(seminar.id, 'archive')}
            >
              <Archive size={12} /> Archive
            </Button>
          )}
          {['draft', 'scheduled'].includes(seminar.status) && (
            <Button
              size="sm"
              variant="outline"
              className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10 text-xs h-8 gap-1"
              onClick={async () => {
                const { default: axios } = await import('axios');
                try {
                  await axios.put(`${API}/admin/live-seminars/${seminar.id}`, { status: 'scheduled' }, { headers: authHeaders });
                  onSetStatus(seminar.id + '__noop', '');
                } catch (_e) {}
              }}
            >
              Schedule
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-600 mt-2">
          Current: <span className="text-gray-400">{seminar.status}</span>
          {seminar.started_at && ` · Started ${new Date(seminar.started_at).toLocaleString()}`}
          {seminar.ended_at && ` · Ended ${new Date(seminar.ended_at).toLocaleString()}`}
        </p>
      </div>

      {/* Chat Moderation */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Chat Moderation</p>
          <Button
            size="sm"
            variant="outline"
            className="border-white/20 text-gray-400 hover:bg-white/10 text-xs h-7"
            onClick={() => onFetchChat(seminar.id)}
          >
            <Eye size={12} className="mr-1" /> Load Messages
          </Button>
        </div>

        {chatTab === seminar.id && chatMessages.length === 0 && (
          <p className="text-gray-600 text-xs">No messages yet.</p>
        )}
        {chatTab === seminar.id && chatMessages.length > 0 && (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {chatMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex items-start gap-2 p-2 rounded-sm border text-xs ${
                  msg.status === 'hidden' ? 'opacity-40 border-white/5 bg-white/2' :
                  msg.status === 'answered' ? 'border-green-500/20 bg-green-500/5' :
                  msg.type === 'question' ? 'border-yellow-500/20 bg-yellow-500/5' :
                  'border-white/10 bg-white/3'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-gray-300">{msg.user_name}: </span>
                  {msg.type === 'question' && <span className="text-yellow-400 mr-1">[Q]</span>}
                  <span className="text-gray-400 break-words">{msg.message}</span>
                  <span className="ml-2 text-gray-600">[{msg.status}]</span>
                </div>
                {msg.status === 'visible' && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => onHide(seminar.id, msg.id)}
                      className="text-red-400 hover:text-red-300 text-xs px-1.5 py-0.5 border border-red-500/30 rounded-sm"
                      title="Hide"
                    >
                      Hide
                    </button>
                    {msg.type === 'question' && (
                      <button
                        onClick={() => onMarkAnswered(seminar.id, msg.id)}
                        className="text-green-400 hover:text-green-300 text-xs px-1.5 py-0.5 border border-green-500/30 rounded-sm"
                        title="Mark answered"
                      >
                        ✓
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveSeminarsManagement;
